import React, { useState, useEffect } from 'react';
import { get, post } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function SupplierDashboard() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    get('/supplier-dashboard').then(d => setSuppliers(d.data||[])).finally(() => setLoading(false));
  }, []);

  const loadDetail = (id) => {
    setDetailLoading(true);
    get('/supplier-dashboard/'+id).then(setDetail).finally(() => setDetailLoading(false));
  };

  const handleSelect = (s) => {
    setSelected(s);
    loadDetail(s.id);
  };

  const filtered = search
    ? suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || (s.category||'').toLowerCase().includes(search.toLowerCase()))
    : suppliers;

  const totalOwing = suppliers.reduce((sum,s) => sum + parseFloat(s.total_owing||0), 0);
  const totalSpent = suppliers.reduce((sum,s) => sum + parseFloat(s.total_paid||0), 0);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Supplier Dashboard</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Spending and payment summary per supplier</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="metrics" style={{gridTemplateColumns:'repeat(3,1fr)',marginBottom:20}}>
        <div className="tile">
          <div className="tile-lbl">Total Suppliers</div>
          <div className="tile-val">{suppliers.length}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Total Spent (All Time)</div>
          <div className="tile-val warn">{fmt(totalSpent)}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Total Outstanding</div>
          <div className="tile-val neg">{fmt(totalOwing)}</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:selected?'1fr 1.5fr':'1fr',gap:16}}>

        {/* Supplier list */}
        <div>
          <div className="search-bar" style={{marginBottom:12}}>
            <input placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="tbl-wrap">
            {loading ? <div className="spinner"></div> : (
              <table>
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th style={{textAlign:'right'}}>Total Billed</th>
                    <th style={{textAlign:'right'}}>Owing</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0
                    ? <tr><td colSpan="4"><div className="empty-state"><h3>No suppliers yet</h3></div></td></tr>
                    : filtered.map(s => (
                      <tr key={s.id} style={{background:selected?.id===s.id?'#F2EFE7':''}}>
                        <td>
                          <div style={{fontWeight:500,fontSize:13}}>{s.name}</div>
                          <div className="text-muted text-xs">{s.category||'—'}</div>
                        </td>
                        <td className="amt">{fmt(s.total_billed)}</td>
                        <td className="amt" style={{color:parseFloat(s.total_owing)>0?'var(--unpaid)':'var(--paid)'}}>
                          {fmt(s.total_owing)}
                        </td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleSelect(s)}>
                            {selected?.id===s.id ? 'Close' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Supplier detail */}
        {selected && (
          <div>
            {detailLoading ? <div className="spinner"></div> : detail && (
              <div>
                {/* Supplier header */}
                <div style={{background:'#556B2F',borderRadius:10,padding:'16px 20px',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'#A8893D'}}>{detail.supplier.name}</div>
                    <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>{detail.supplier.category||''} {detail.supplier.phone?'| '+detail.supplier.phone:''}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{color:'rgba(242,239,231,.5)'}} onClick={() => { setSelected(null); setDetail(null); }}>X</button>
                </div>

                {/* Summary tiles */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
                  <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
                    <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Total Billed</div>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'#3B2A21'}}>{fmt(detail.summary.total_billed)}</div>
                  </div>
                  <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
                    <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Total Paid</div>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:'var(--paid)'}}>{fmt(detail.summary.total_paid)}</div>
                  </div>
                  <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
                    <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Outstanding</div>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:600,color:parseFloat(detail.summary.total_owing)>0?'var(--unpaid)':'var(--paid)'}}>{fmt(detail.summary.total_owing)}</div>
                  </div>
                </div>

                {/* Bank details */}
                {detail.supplier.bank_name && (
                  <div style={{background:'#F2EFE7',borderRadius:8,padding:'12px 16px',marginBottom:14,fontSize:13}}>
                    <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:8}}>Bank Details</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div><span style={{color:'#8A8A8A'}}>Bank: </span><strong>{detail.supplier.bank_name}</strong></div>
                      <div><span style={{color:'#8A8A8A'}}>Account No: </span><strong>{detail.supplier.bank_account_no}</strong></div>
                      <div style={{gridColumn:'1/-1'}}><span style={{color:'#8A8A8A'}}>Account Name: </span><strong>{detail.supplier.bank_account_name}</strong></div>
                    </div>
                  </div>
                )}

                {/* Monthly spend chart */}
                {detail.monthly_spend.length > 0 && (
                  <div className="card" style={{marginBottom:14}}>
                    <div className="card-hdr"><span className="card-title">Monthly Spend</span></div>
                    <div style={{padding:14}}>
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={[...detail.monthly_spend].reverse()}>
                          <XAxis dataKey="month" tick={{fontSize:10}} />
                          <YAxis tick={{fontSize:10}} tickFormatter={v=>'\u20a6'+(v/1000).toFixed(0)+'k'} />
                          <Tooltip formatter={v=>fmt(v)} />
                          <Bar dataKey="total" name="Spent" fill="#556B2F" radius={[3,3,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Unpaid bills */}
                {detail.bills.filter(b => b.status !== 'paid' && b.status !== 'cancelled').length > 0 && (
                  <div className="card" style={{marginBottom:14}}>
                    <div className="card-hdr"><span className="card-title" style={{color:'var(--unpaid)'}}>Outstanding Bills</span></div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'var(--cream)'}}>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Bill #</th>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Date</th>
                          <th style={{padding:'8px 14px',textAlign:'right',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Amount</th>
                          <th style={{padding:'8px 14px',textAlign:'right',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Owing</th>
                          <th style={{padding:'8px 14px',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.bills.filter(b => b.status !== 'paid' && b.status !== 'cancelled').map(b => (
                          <tr key={b.id}>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{b.bill_number}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)'}}>{b.date}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(b.amount)}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--unpaid)',fontWeight:600}}>{fmt(b.balance_due)}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)'}}>
                              <span className={'badge '+(b.status==='part_paid'?'b-part':'b-unpaid')}>{b.status.replace('_',' ')}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Payment history */}
                {detail.payments.length > 0 && (
                  <div className="card">
                    <div className="card-hdr"><span className="card-title">Payment History</span></div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'var(--cream)'}}>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Ref</th>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Date</th>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Bill</th>
                          <th style={{padding:'8px 14px',textAlign:'left',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Method</th>
                          <th style={{padding:'8px 14px',textAlign:'right',fontSize:9,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.payments.map(p => (
                          <tr key={p.id}>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{p.payment_number}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)'}}>{p.date}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',color:'var(--ink-light)'}}>{p.bill_number}</td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)'}}><span className="badge b-grey">{p.method}</span></td>
                            <td style={{padding:'8px 14px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:13,color:'var(--paid)',fontWeight:600}}>{fmt(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
